// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useEffectExceptOnMount from "@insite/client-framework/Common/Hooks/useEffectExceptOnMount";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addScrollbarGutter, removeScrollbarGutter } from "@tps/XDL/Utilities";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

type Props = PageProps & HasShellContext & ThemeProps<BaseTheme>;

export interface SignInOverlayStyles {
    page?: MobiusPageProps;
    drawer?: DrawerPresentationProps;
    innerWrapper?: InjectableCss;
}

export const signInOverlayStyles: SignInOverlayStyles = {
    page: {
        padding: 0,
    },
    drawer: {
        size: 400,
        closeButtonProps: {
            shape: "pill",
            buttonType: "solid",
            color: "common.background",
            size: 36,
        },
        cssOverrides: {
            drawerTitle: css`
                background: ${getColor("common.background")};
            `,
            drawerBody: css`
                background: ${getColor("common.background")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
};

const InnerWrapper = styled.div<{ isEdit?: boolean } & InjectableCss>`
    padding: ${props => (props.isEdit ? "0" : "0 30px")};
    overflow: ${props => (props.isEdit ? "auto" : "hidden")};
    width: 100%;
    margin: 0 auto;
    ${injectCss}
`;

const styles = signInOverlayStyles;

const SignInOverlay = ({ id, shellContext: { isCurrentPage, isEditing, isInShell } }: Props) => {
    const isShell = isCurrentPage && isInShell;
    const isEdit = isShell && isEditing;
    const isOpen = useAppSelector(state => state.components.signInOverlay.isOpen);
    const dispatch = useDispatch();
    const closeDrawer = () => {
        dispatch(setSignInOverlayIsOpen({ isOpen: false }));
    };

    const location = useAppSelector(o => getLocation(o));
    useEffectExceptOnMount(() => {
        dispatch(setSignInOverlayIsOpen({ isOpen: false }));
    }, [location.pathname]);

    // XNG-Changes add scrollbar gutter and loading
    if (typeof document !== "undefined") {
        if (isOpen) {
            addScrollbarGutter();
            ariaAppHider.hide(document.body);
        } else {
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }
    }
    
    const showLoadingOverlay = useAppSelector(state => state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest);

    return (
        <Page {...styles.page}>
            <Drawer
                {...styles.drawer}
                position="right"
                isOpen={isOpen || isShell}
                handleClose={closeDrawer}
                contentLabel="sign in drawer"
                data-test-selector="signInOverlayDrawer"
            >
                <InnerWrapper isEdit={isEdit} {...styles.innerWrapper}>
                    <Zone contentId={id} zoneName="Content" />
                </InnerWrapper>
            </Drawer>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withTheme(withIsInShell(SignInOverlay)),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
